import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { createContext, createElement, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { applyViewers } from '../features/inbox/viewers.ts';
import { useAuth } from './auth.tsx';
import { useLatest } from './hooks.ts';

export const REALTIME_STREAM_URL = '/api/realtime/stream';

export const REALTIME_EVENT_TYPES = [
  'message.created',
  'conversation.updated',
  'receipt.updated',
  'registration.updated',
  'payment.updated',
  'campaign.progress',
  'connection.health',
  'notification.delivery',
  'ai.draft',
  'bot.reply',
  'presence.updated',
] as const;
export type RealtimeEventType = (typeof REALTIME_EVENT_TYPES)[number];

/** `resync` = server cursor expired, every view must refetch; `*` = every domain event. */
export type RealtimeSubscriptionType = RealtimeEventType | 'resync' | '*';

export type RealtimeConnectionState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'offline';

/** Payload written by apps/server/src/modules/realtime/hub.ts. */
export interface RealtimeEvent<P = Record<string, unknown>> {
  id: number;
  type: RealtimeEventType | 'resync';
  conversationId: string | null;
  entityType: string | null;
  entityId: string | null;
  payload: P;
  at: string;
}

export type RealtimeHandler = (event: RealtimeEvent) => void;

/** Default cache invalidation per event (prefix match on query keys). */
export function queryKeysForEvent(event: Pick<RealtimeEvent, 'type' | 'conversationId' | 'entityId'>): QueryKey[] {
  const c = event.conversationId;
  const e = event.entityId;
  switch (event.type) {
    case 'message.created':
    case 'conversation.updated':
      return c ? [['conversations'], ['conversation', c], ['messages', c]] : [['conversations']];
    case 'receipt.updated':
      return c ? [['conversations'], ['conversation', c], ['messages', c], ['deliveries']] : [['conversations'], ['deliveries']];
    case 'registration.updated':
    case 'payment.updated':
      return [['registrations'], ['contacts'], ['payments']];
    case 'campaign.progress':
      return e ? [['campaigns'], ['campaign', e]] : [['campaigns']];
    case 'connection.health':
      return [['connections'], ['system-health']];
    case 'notification.delivery':
      return [['notifications']];
    case 'ai.draft':
      return c ? [['ai-drafts', c], ['conversation', c]] : [];
    case 'bot.reply':
      return c ? [['bot', 'drafts', c]] : [];
    case 'presence.updated':
      return [['presence']];
    default:
      return [];
  }
}

class RealtimeBus {
  private readonly handlers = new Map<string, Set<RealtimeHandler>>();
  private readonly stateListeners = new Set<() => void>();
  private state: RealtimeConnectionState = 'idle';
  lastEventId: string | null = null;

  on(type: string, handler: RealtimeHandler): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler);
    return () => {
      set.delete(handler);
    };
  }

  emit(event: RealtimeEvent): void {
    const targets = event.type === 'resync' ? ['resync'] : [event.type, '*'];
    for (const type of targets) {
      for (const handler of this.handlers.get(type) ?? []) {
        try {
          handler(event);
        } catch (err) {
          console.error('realtime handler failed', err);
        }
      }
    }
  }

  setState(next: RealtimeConnectionState): void {
    if (next === this.state) return;
    this.state = next;
    for (const l of this.stateListeners) l();
  }

  readonly subscribeState = (listener: () => void): (() => void) => {
    this.stateListeners.add(listener);
    return () => {
      this.stateListeners.delete(listener);
    };
  };

  readonly getState = (): RealtimeConnectionState => this.state;
}

const RealtimeContext = createContext<RealtimeBus | null>(null);

/** Opens the SSE stream while a fully authenticated session exists. */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { status, refresh } = useAuth();
  const queryClient = useQueryClient();
  const [bus] = useState(() => new RealtimeBus());
  const refreshRef = useLatest(refresh);
  const enabled = status === 'authenticated';

  useEffect(() => {
    if (!enabled) {
      bus.lastEventId = null;
      bus.setState('idle');
      return;
    }
    return connectRealtime(bus, queryClient, () => refreshRef.current());
  }, [enabled, bus, queryClient, refreshRef]);

  return createElement(RealtimeContext.Provider, { value: bus }, children);
}

function connectRealtime(bus: RealtimeBus, queryClient: QueryClient, refreshSession: () => Promise<unknown>): () => void {
  let source: EventSource | null = null;
  let disposed = false;
  let retryTimer: number | undefined;
  let flushTimer: number | undefined;
  let attempt = 0;
  // Set after any disconnect: active views refetch once the stream is healthy again.
  let missedUpdates = false;
  const pending = new Map<string, QueryKey>();

  // Bursts (e.g. a campaign sending hundreds of messages) collapse into one invalidation per key.
  const invalidate = (keys: QueryKey[]) => {
    for (const key of keys) pending.set(JSON.stringify(key), key);
    if (flushTimer !== undefined || pending.size === 0) return;
    flushTimer = window.setTimeout(() => {
      flushTimer = undefined;
      const batch = [...pending.values()];
      pending.clear();
      for (const queryKey of batch) void queryClient.invalidateQueries({ queryKey });
    }, 120);
  };

  const resyncActive = () => {
    void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'me', refetchType: 'active' });
  };

  const handleEvent = (ev: MessageEvent<string>) => {
    if (ev.lastEventId) bus.lastEventId = ev.lastEventId;
    let data: RealtimeEvent;
    try {
      data = JSON.parse(ev.data) as RealtimeEvent;
    } catch {
      return;
    }
    invalidate(queryKeysForEvent(data));
    bus.emit(data);
  };

  const scheduleReconnect = () => {
    if (disposed || retryTimer !== undefined || source) return;
    attempt += 1;
    const delay = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5)) * (0.75 + Math.random() * 0.5);
    retryTimer = window.setTimeout(() => {
      retryTimer = undefined;
      connect();
    }, delay);
  };

  const connect = () => {
    if (disposed || source) return;
    if (!navigator.onLine) {
      missedUpdates = true;
      bus.setState('offline');
      return;
    }
    bus.setState(attempt === 0 && !missedUpdates ? 'connecting' : 'reconnecting');
    // A new EventSource cannot send Last-Event-ID itself; the server also accepts ?lastEventId=.
    const url = bus.lastEventId ? `${REALTIME_STREAM_URL}?lastEventId=${encodeURIComponent(bus.lastEventId)}` : REALTIME_STREAM_URL;
    const es = new EventSource(url, { withCredentials: true });
    source = es;

    es.onopen = () => {
      attempt = 0;
      bus.setState('open');
      if (missedUpdates) {
        missedUpdates = false;
        resyncActive();
      }
    };

    es.onerror = () => {
      if (source !== es) return;
      missedUpdates = true;
      if (es.readyState === EventSource.CLOSED) {
        // The browser gave up (HTTP error such as 401/503): verify the session, then back off.
        source = null;
        bus.setState(navigator.onLine ? 'reconnecting' : 'offline');
        void refreshSession()
          .catch(() => undefined)
          .finally(scheduleReconnect);
      } else {
        // Still CONNECTING: the browser retries on its own and sends Last-Event-ID.
        bus.setState(navigator.onLine ? 'reconnecting' : 'offline');
      }
    };

    for (const type of REALTIME_EVENT_TYPES) es.addEventListener(type, handleEvent as EventListener);

    es.addEventListener('ready', ((ev: MessageEvent<string>) => {
      try {
        const data = JSON.parse(ev.data) as { lastEventId?: number | null };
        if (data.lastEventId != null && !bus.lastEventId) bus.lastEventId = String(data.lastEventId);
      } catch {
        // ignore malformed ready payloads
      }
    }) as EventListener);

    /*
     * Who has which chat open (v1.84.0). Not a domain event: it is only true right now, it is never stored,
     * and it must not make anything refetch — the frame carries the names and the rows repaint from them.
     */
    es.addEventListener('viewers', ((ev: MessageEvent<string>) => {
      try {
        const data = JSON.parse(ev.data) as { conversationId?: string; viewers?: Array<{ userId: string; displayName: string }> };
        if (data.conversationId) applyViewers(data.conversationId, data.viewers ?? []);
      } catch {
        // ignore malformed frames
      }
    }) as EventListener);

    es.addEventListener('resync', () => {
      resyncActive();
      bus.emit({ id: 0, type: 'resync', conversationId: null, entityType: null, entityId: null, payload: {}, at: new Date().toISOString() });
    });

    es.addEventListener('session_expired', () => {
      es.close();
      if (source === es) source = null;
      missedUpdates = true;
      bus.setState('reconnecting');
      // If the session is really gone, `me` becomes null and the provider tears this connection down.
      void refreshSession()
        .catch(() => undefined)
        .finally(scheduleReconnect);
    });
  };

  const reconnectNow = () => {
    window.clearTimeout(retryTimer);
    retryTimer = undefined;
    attempt = 0;
    connect();
  };

  const onOnline = () => {
    if (disposed) return;
    if (!source) reconnectNow();
    else if (source.readyState === EventSource.OPEN) {
      bus.setState('open');
      if (missedUpdates) {
        missedUpdates = false;
        resyncActive();
      }
    }
  };

  const onOffline = () => {
    missedUpdates = true;
    bus.setState('offline');
  };

  const onVisibleOrFocus = () => {
    if (disposed || document.visibilityState !== 'visible') return;
    if (!source) reconnectNow();
    else if (missedUpdates && source.readyState === EventSource.OPEN) {
      missedUpdates = false;
      resyncActive();
    }
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  window.addEventListener('focus', onVisibleOrFocus);
  document.addEventListener('visibilitychange', onVisibleOrFocus);
  connect();

  return () => {
    disposed = true;
    window.clearTimeout(retryTimer);
    window.clearTimeout(flushTimer);
    source?.close();
    source = null;
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    window.removeEventListener('focus', onVisibleOrFocus);
    document.removeEventListener('visibilitychange', onVisibleOrFocus);
    bus.setState('idle');
  };
}

function useBus(): RealtimeBus {
  const bus = useContext(RealtimeContext);
  if (!bus) throw new Error('Realtime hooks must be used inside <RealtimeProvider>');
  return bus;
}

/** Subscribe to one event type ('*' for all). The handler may change between renders. */
export function useRealtimeEvent(type: RealtimeSubscriptionType, handler: RealtimeHandler): void {
  const bus = useBus();
  const latest = useLatest(handler);
  useEffect(() => bus.on(type, (event) => latest.current(event)), [bus, type, latest]);
}

export function useRealtimeState(): RealtimeConnectionState {
  const bus = useBus();
  return useSyncExternalStore(bus.subscribeState, bus.getState, bus.getState);
}
